import React from 'react';
import { useProvider, useHasAccess } from '~/Hooks';
import { IItemCreationSubsectionInputProps } from '~/Components';
import { ExtendedEventGroup } from '~/Screens/Events/EventCreate/ExtendedEventGroup.model';
import { EventCancellation } from '~/Components/Icons';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { TimeSelector } from '~/Screens/Events/EventCreate/EventCreateSections/customInputs/TimeSelector';
import { WaitingList } from '~/Components/Icons/WaitingList';
import { BookingWindow } from '~/Components/Icons/BookingWindow';
import { useTranslation } from 'react-i18next';
import { ExtendedEvent } from '~/Screens/Events/EventCreate/ExtendedEvent.model';
import { DifferenceFormat } from '~/Models/DTO/Time.model';
import { MAX_CANCELATION_WINDOW_INPUT_USC } from '~/Screens/Events/constants';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
export interface IUseCancellationWindowInputsProps {
  eventGroup: ExtendedEventGroup;
  event: ExtendedEvent | undefined;
  notEventGroupOrFreePackage: boolean;
}

export const useCancellationWindowInputs = ({
  eventGroup,
  notEventGroupOrFreePackage,
  event,
}: IUseCancellationWindowInputsProps) => {
  const { isUsingThePartnerTool } = useProvider();
  const { t } = useTranslation();
  const { hasAccess } = useHasAccess();

  const compareStartAndEndFields = () => {
    const MINUTES_TO_HOURS_FACTOR = 60;
    const NEUTRAL_FACTOR = 1;

    const {
      publicBookingWindowStartAsNumber,
      publicBookingWindowEndAsNumber,
      publicBookingWindowEndType,
      publicBookingWindowStartType,
    } = eventGroup;

    const { HOURS } = DifferenceFormat;

    return (
      (publicBookingWindowStartAsNumber || 0) *
        (publicBookingWindowStartType === HOURS ? MINUTES_TO_HOURS_FACTOR : NEUTRAL_FACTOR) <
      (publicBookingWindowEndAsNumber || 0) *
        (publicBookingWindowEndType === HOURS ? MINUTES_TO_HOURS_FACTOR : NEUTRAL_FACTOR)
    );
  };

  const onlineCancelationInputHasError = (publicCancelationNumber = 0) => {
    const validationForPartnerTool =
      isUsingThePartnerTool && publicCancelationNumber > MAX_CANCELATION_WINDOW_INPUT_USC;

    return publicCancelationNumber <= 0 || validationForPartnerTool;
  };

  const freeCancellationInputs: IItemCreationSubsectionInputProps<Partial<ExtendedEventGroup>>[] = [
    {
      icon: <EventCancellation />,
      label: isUsingThePartnerTool
        ? t('event.creation.settings.onlineCancellation')
        : t('app.event.onlineCancellation'),
      type: ItemCreationValidInput.SWITCHER,
      name: 'publicCancellationWindowOpen',
      disabled: !!event,
      children: [
        {
          hidden: !eventGroup.publicCancellationWindowOpen,
          customElement: (
            <TimeSelector
              isDisabled={!!event}
              hasAnError={onlineCancelationInputHasError(
                eventGroup.publicCancellationRefundEndAsNumber,
              )}
              name="publicCancellationRefundEndAsNumber"
              label={
                isUsingThePartnerTool ? t('app.event.cancelEvent.refund') : t('app.event.refund')
              }
              type="publicCancellationRefundEndType"
              max={(isUsingThePartnerTool && MAX_CANCELATION_WINDOW_INPUT_USC) || undefined}
              useOnlyHours={
                eventGroup.isUSCIntegrationEnabled ||
                hasAccess(AccessList.CANCELATION_WINDOW_PARTNER_TOOL)
              }
            />
          ),
        },
      ],
    },
  ];

  const paidCancellationInputs: IItemCreationSubsectionInputProps<Partial<ExtendedEventGroup>>[] = [
    {
      icon: <WaitingList />,
      label: t('event.creation.settings.waitingList'),
      type: ItemCreationValidInput.SWITCHER,
      name: 'waitingListEnabled',
      disabled: notEventGroupOrFreePackage,
      hidden: isUsingThePartnerTool,
    },
    {
      icon: <BookingWindow />,
      label: t('event.creation.settings.bookingWindow'),
      type: ItemCreationValidInput.SWITCHER,
      name: 'bookingWindowOpen',
      disabled: notEventGroupOrFreePackage,
      hidden: isUsingThePartnerTool,
      children: [
        {
          hidden: !eventGroup.bookingWindowOpen,
          customElement: (
            <TimeSelector
              isDisabled={!!event}
              hasAnError={compareStartAndEndFields()}
              name="publicBookingWindowStartAsNumber"
              label={t('app.from')}
              type="publicBookingWindowStartType"
            />
          ),
        },
        {
          hidden: !eventGroup.bookingWindowOpen,
          customElement: (
            <TimeSelector
              isDisabled={!!event}
              hasAnError={compareStartAndEndFields()}
              name="publicBookingWindowEndAsNumber"
              label={t('app.until')}
              type="publicBookingWindowEndType"
            />
          ),
        },
      ],
    },
  ];

  return {
    freeCancellationInputs,
    paidCancellationInputs,
    allCancellationInputs: [...freeCancellationInputs, ...paidCancellationInputs],
  };
};
